import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit2, Trash2, Search, Package } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loading } from '../../components/Loading';
import { ImageUploader } from '../../components/ImageUploader';
import { getProducts, addProduct, updateProduct, deleteProduct, getCategories } from '../../utils/firestoreService';

export const AdminProducts = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    imageUrl: '',
    images: [],
    stock: '',
    featured: false,
    isActive: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsData, categoriesData] = await Promise.all([
        getProducts(null, true),
        getCategories(),
      ]);
      
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setProducts([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Debes estar autenticado');
      return;
    }

    try {
      const cleanedImages = formData.images.filter(Boolean);
      // Mantener imageUrl para compatibilidad hacia atrás
      const finalImageUrl = cleanedImages.length > 0 ? cleanedImages[0] : formData.imageUrl;
      
      const productData = {
        ...formData,
        imageUrl: finalImageUrl,
        images: cleanedImages,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, productData);
        toast.success('Producto actualizado correctamente');
      } else {
        await addProduct(productData);
        toast.success('Producto creado correctamente');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Error al guardar el producto. Verifica las reglas de Firestore.');
    }
  };

  const handleDelete = async (productId) => {
    if (!user) {
      toast.error('Debes estar autenticado');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;

    try {
      await deleteProduct(productId);
      toast.success('Producto eliminado correctamente');
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Error al eliminar el producto');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      imageUrl: product.imageUrl,
      images: product.images || (product.imageUrl ? [product.imageUrl] : []),
      stock: product.stock.toString(),
      featured: product.featured || false,
      isActive: product.isActive !== false,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      imageUrl: '',
      images: [],
      stock: '',
      featured: false,
      isActive: true,
    });
  };

  const filteredProducts = Array.isArray(products) 
    ? products.filter((product) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  if (loading) return <Loading />;

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-fredoka font-bold text-[#1A1A2E]" data-testid="admin-products-title">
          Productos
        </h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <button
              className="bg-[#00C2CB] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#00C2CB]/90 transition-all flex items-center gap-2"
              data-testid="add-product-button"
            >
              <Plus className="w-5 h-5" />
              Nuevo Producto
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-fredoka text-2xl">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Descripción *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  data-testid="product-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Precio *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="stock">Stock *</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    required
                    data-testid="product-stock-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="category">Categoría *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Imágenes del Producto (Hasta 3) *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
                  {[0, 1, 2].map((index) => (
                    <div key={index} className="space-y-2">
                      <Label className="text-xs text-slate-500">
                        {index === 0 ? 'Principal (Obligatoria)' : `Opcional ${index}`}
                      </Label>
                      <ImageUploader
                        id={`image-upload-${index}`}
                        currentImage={formData.images[index] || (index === 0 && !formData.images.length ? formData.imageUrl : '')}
                        onImageUploaded={(url) => {
                          const newImages = [...formData.images];
                          if (index === 0 && !newImages.length && formData.imageUrl) {
                            newImages[0] = formData.imageUrl;
                          }
                          if (url) {
                            newImages[index] = url;
                          } else {
                            newImages[index] = '';
                          }
                          setFormData({ ...formData, images: newImages });
                        }}
                        applyWatermark={true}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#9CA3AF] mt-2 font-quicksand">
                  Las imágenes se subirán a Firebase Storage con la marca de agua aplicada.
                </p>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <input
                    id="featured"
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-4 h-4 rounded"
                    data-testid="product-featured-checkbox"
                  />
                  <Label htmlFor="featured" className="cursor-pointer">
                    Producto destacado
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 rounded"
                    data-testid="product-active-checkbox"
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">
                    Producto activo
                  </Label>
                </div>
              </div>
              <button
                type="submit"
                className="w-full bg-[#FF6B00] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#FF6B00]/90 transition-all"
                data-testid="product-submit-button"
              >
                {editingProduct ? 'Actualizar' : 'Crear'} Producto
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
          <Input
            type="text"
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl"
            data-testid="product-search-input"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {filteredProducts.length === 0 && !loading ? (
          <div className="text-center py-20 px-4">
            <Package className="w-16 h-16 mx-auto mb-4 text-[#9CA3AF]" />
            <h3 className="text-xl font-fredoka font-bold text-[#1A1A2E] mb-2">
              No hay productos aún
            </h3>
            <p className="text-[#4A4A6A] font-quicksand mb-6">
              {categories.length === 0 
                ? 'Primero crea algunas categorías, luego podrás agregar productos.'
                : 'Comienza agregando tu primer producto.'}
            </p>
            {categories.length > 0 && (
              <button
                onClick={() => setDialogOpen(true)}
                className="bg-[#00C2CB] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#00C2CB]/90 transition-all"
                data-testid="empty-state-add-product"
              >
                Agregar Primer Producto
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left font-fredoka font-bold text-[#1A1A2E]">Imagen</th>
                <th className="px-6 py-4 text-left font-fredoka font-bold text-[#1A1A2E]">Nombre</th>
                <th className="px-6 py-4 text-left font-fredoka font-bold text-[#1A1A2E]">Precio</th>
                <th className="px-6 py-4 text-left font-fredoka font-bold text-[#1A1A2E]">Stock</th>
                <th className="px-6 py-4 text-left font-fredoka font-bold text-[#1A1A2E]">Categoría</th>
                <th className="px-6 py-4 text-left font-fredoka font-bold text-[#1A1A2E]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`product-row-${product.id}`}>
                  <td className="px-6 py-4">
                    <img src={product.images && product.images.length > 0 ? product.images[0] : product.imageUrl} alt={product.name} className="w-16 h-16 object-cover rounded-lg" />
                  </td>
                  <td className="px-6 py-4 font-quicksand">
                    <div className="flex items-center gap-2">
                      {product.name}
                      {product.isActive === false && (
                        <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full font-bold">
                          Inactivo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 font-quicksand">${product.price.toLocaleString()}</td>
                  <td className="px-6 py-4 font-quicksand">{product.stock}</td>
                  <td className="px-6 py-4 font-quicksand">{categories.find((c) => c.id === product.category)?.name || product.category}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditDialog(product)}
                        className="p-2 text-[#00C2CB] hover:bg-[#00C2CB]/10 rounded-lg"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
};